import { createFileRoute, redirect } from "@tanstack/react-router";

import { sdk } from "@/api/sdk";
import { PageLayout } from "@/components/layouts/page/layout";
import { connectSessionSocket, disconnectSessionSocket } from "@/features/auth/modules/session-socket/session-socket";
import { guardLegalConsents } from "@/features/legal/utils/legal-consent-gate";
import { alignAccountLocale } from "@/features/settings/utils/account-locale";

export const Route = createFileRoute("/(private)")({
  // Everything behind the login: no crawler can see it, so none should list it.
  staticData: { sitemap: false },
  component: PageLayout,
  // The socket follows the session: opened when the signed-in area is entered, closed when it
  // is left (sign-out, expired session). See features/auth/modules/session-socket/session-socket.ts.
  onEnter: connectSessionSocket,
  onLeave: disconnectSessionSocket,
  // Browser only, and every route under it inherits this. With SSR on, this
  // beforeLoad also ran on the server, where the session cookie is not on the
  // request, so every reload of a signed-in page bounced to /login and the
  // login page then said "You're signed in". Nothing behind the guard needs a
  // server render anyway.
  ssr: false,
  beforeLoad: async ({ location }) => {
    const { data } = await sdk.users.me.$get.send();

    if (!data) {
      throw redirect({ to: "/login", search: { redirect: location.href } });
    }

    // The language saved on the account follows the person onto this device (and back).
    await alignAccountLocale(data.user);

    // Agreeing to the documents in force comes before anything else behind the login.
    await guardLegalConsents({ userId: data.user.id, location });

    const hasOrganization = Boolean(data.organizationId) && (data.organizations?.length ?? 0) > 0;
    if (!hasOrganization) {
      throw redirect({
        to: "/onboarding",
        search: location.pathname === "/" ? {} : { redirect: location.pathname },
      });
    }

    return { user: data };
  },
});
